import React from 'react';
import { useLabels } from 'wsm-common-data';
import { getPriceSelection } from 'wsm-pricing-display';
import vehiclePropType from '../../propTypes/vehiclePropType';
import { addImageParams } from '../../utilities/images';
import { pricingMap } from '../../utilities/pricingMap';

/**
 * Static, SSR-safe vehicle card for AI-bot traffic (US1994526 / TA4063706).
 *
 * Deliberately presentational and dependency-light: no react-slick carousel,
 * no IntersectionObserver, no localStorage, no Redux store, no stateful or
 * browser-only hooks — only the render-phase, SSR-safe useLabels (for the
 * localized "Please Call" label, same pattern as SeoVehicleCards / SeoPagination).
 * It renders only the four crawlable fields ADR-0014 calls for — image (+ alt),
 * title, final
 * price, and the VDP link. It reuses the real card's Kinetic utility/layout
 * classes (vehicle-card-grid / box / vehicle-card-detailed / img-container /
 * vehicle-card-title, etc.) so it visually resembles the interactive cards,
 * while staying static so it survives renderToString and hydrates cleanly (the
 * SAME component renders from the SAME seeded data on the server and on the
 * client's first render).
 *
 * On the client, after hydration, the existing window.DDC.InvData + pubsub
 * path swaps this out for the full interactive VehicleCard.
 */

// Title arrives from WIS as either a string or a [subtitle, maintitle] pair
// (mirrors the formatTitle logic in vehicleCard/detailsSection/VehicleTitle.jsx).
export const formatSeoTitle = (title) => {
	let joined;
	if (Array.isArray(title)) {
		const [subtitle, maintitle] = title;
		joined =
			maintitle === undefined ? subtitle : `${subtitle} ${maintitle}`;
	} else {
		joined = title;
	}
	// Collapse the whitespace runs the WIS [subtitle, maintitle] join produces
	// (e.g. "Toyota   4Runner") so the crawlable text is clean. Browsers collapse
	// this visually anyway; we normalize it in the markup for the crawler.
	return joined ? joined.replace(/\s+/g, ' ').trim() : joined;
};

// Final price = the dPrice entry flagged isFinalPrice, selected via the SAME
// helper the real SRP card's PriceToggle uses (wsm-pricing-display's
// getPriceSelection), so the two can't drift. pricingMap maps the legacy GVM
// `dprice` to the canonical `dPrice`; `false` selects dPrice (the bot path has
// no ePrice). WIS has already localized the value (e.g. "$50,925").
export const getFinalPrice = (pricing = {}) => {
	const { finalPriceItem } = getPriceSelection(pricingMap(pricing), false);
	return finalPriceItem?.value;
};

const SeoVehicleCard = ({ vehicle }) => {
	const labels = useLabels();
	const { link, title, pricing, images } = vehicle;
	const image = (images && images[0]) || {};
	const formattedTitle = formatSeoTitle(title);
	// Mirror the SRP card's PriceToggle: show the final price; if there's no
	// final-price item (empty pricing, or no isFinalPrice entry) or its value is
	// the PLEASE_CALL sentinel, show the localized "Please Call" instead.
	const finalPrice = getFinalPrice(pricing);
	const priceText =
		finalPrice && finalPrice !== 'PLEASE_CALL'
			? finalPrice
			: labels.get('PLEASE_CALL');
	// WIS supplies a localized alt string; fall back to the image title, then
	// the vehicle title, so the <img> is never altless for a crawler.
	const altText = image.alt || image.title || formattedTitle;

	return (
		<li className="seo-vehicle-card box box-border vehicle-card vehicle-card-detailed">
			{image.uri && (
				<div className="vehicle-card-media-container">
					<a href={link} className="d-block">
						<div className="img-container img-placeholder img-loaded">
							<img
								className="w-100"
								src={addImageParams(image.uri)}
								alt={altText}
								style={{ aspectRatio: '4 / 3' }}
							/>
						</div>
					</a>
				</div>
			)}
			<div className="vehicle-card-details-container">
				<h2 className="vehicle-card-title mt-0 mb-0 d-block ddc-font-size-large">
					<a href={link}>{formattedTitle}</a>
				</h2>
				<div className="ddc-font-size-large font-weight-bold mt-2">
					{priceText}
				</div>
			</div>
		</li>
	);
};

SeoVehicleCard.propTypes = {
	vehicle: vehiclePropType.isRequired
};

export default SeoVehicleCard;
